import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@one-thd/sui-atomic-components';
import { DeliveryExpress } from '@one-thd/sui-icons';
import { BoltIcon } from './BoltIcon';

const PRIORITY_DELIVERY_TYPES = {
  SAME_DAY: 'same-day',
  ON_DEMAND: 'on-demand'
};

export const getPriorityDeliveryGroups = (priorityDeliveryOptions = []) => {
  if (!Array.isArray(priorityDeliveryOptions) || priorityDeliveryOptions.length === 0) {
    return {
      sameDayPriorityOption: null,
      additionalPriorityDeliveryOptions: []
    };
  }

  return {
    sameDayPriorityOption: priorityDeliveryOptions.find((option) => option?.type === PRIORITY_DELIVERY_TYPES.SAME_DAY) || null,
    additionalPriorityDeliveryOptions: priorityDeliveryOptions.filter((option) => option?.type !== PRIORITY_DELIVERY_TYPES.SAME_DAY)
  };
};

const formatDollarAmount = (charge, options = {}) => {
  const {
    showChargeDelta = false,
    hideCents = false
  } = options;

  if (!charge) {
    return '';
  }

  const chargeString = String(charge);
  const parsedCharge = Number(chargeString);
  let formattedCharge = chargeString;

  if (!Number.isNaN(parsedCharge)) {
    if (hideCents) {
      formattedCharge = String(Math.trunc(parsedCharge));
    } else {
      const truncatedCharge = Math.trunc(parsedCharge * 100) / 100;
      formattedCharge = truncatedCharge.toFixed(2);
    }
  }

  return `${showChargeDelta ? '+' : ''}$${formattedCharge}`;
};

const formatTimelineLabel = (timeline) => {
  if (!timeline || timeline.toLowerCase() === 'today') {
    return 'Today';
  }
  return timeline;
};

const formatOnDemandLabel = (timeline) => {
  const optionTimeline = formatTimelineLabel(timeline);
  const hourMatch = optionTimeline.match(/^(\d+)\s+hours?$/i);
  if (hourMatch) {
    return `${hourMatch[1]}-Hour Delivery`;
  }
  return `${optionTimeline} Delivery`;
};

const PriorityDeliveryOptionRow = ({ option = null, showChargeDelta = false, showDivider = true }) => {
  const optionLabel = formatOnDemandLabel(option?.timeline);
  const isOnDemandOption = option?.type === PRIORITY_DELIVERY_TYPES.ON_DEMAND;

  return (
    <>
      <div className="sui-flex sui-items-center sui-justify-between sui-px-3 sui-py-2">
        <Typography variant="body-base">
          <span className="sui-inline-flex sui-items-center sui-gap-2">
            <span className="sui-inline-flex sui-shrink-0" data-testid={isOnDemandOption ? 'delivery-bolt-icon' : 'delivery-express-priority-option-icon'}>
              {isOnDemandOption ? <BoltIcon /> : <DeliveryExpress size="small" />}
            </span>
            <span className="sui-font-bold">{optionLabel}</span>
          </span>
        </Typography>
        <Typography variant="body-base">{formatDollarAmount(option?.totalCharge, { showChargeDelta: showChargeDelta && isOnDemandOption })}</Typography>
      </div>
    </>
  );
};

PriorityDeliveryOptionRow.propTypes = {
  option: PropTypes.shape({
    timeline: PropTypes.string,
    totalCharge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string
  }),
  showChargeDelta: PropTypes.bool
};

export const PriorityDeliveryMessageCard = ({
  additionalPriorityDeliveryOptions = [],
  bodfsLatestDelivery = '8pm',
  fsdbmProgramInclusion = null,
  sameDayPriorityOption = null
}) => {
  return (
    <div className="sui-pt-4" data-testid="delivery-message-under-tile">
      <div className="sui-rounded-lg sui-border-solid sui-border-1 sui-m-1px sui-border-primary">
        <div className="sui-rounded-t-lg sui-flex sui-items-center sui-justify-between sui-px-3 sui-py-2 sui-bg-subtle">
          <Typography variant="h5" className="sui-font-bold">Get It Faster *</Typography>
          <Typography variant="body-xs">*Schedule in Checkout</Typography>
        </div>
        <hr className="sui-my-0" />
        {additionalPriorityDeliveryOptions.map((option, index) => (
          <PriorityDeliveryOptionRow
            key={`${option?.type || 'priority-delivery'}-${index}`}
            option={option}
            showChargeDelta={!sameDayPriorityOption}
          />
        ))}

        {fsdbmProgramInclusion && (
          <div
            className={[
              'sui-px-2',
              additionalPriorityDeliveryOptions?.length ? 'sui-pt-0' : 'sui-pt-2',
              sameDayPriorityOption ? 'sui-pb-0' : 'sui-pb-2'
            ].join(' ')}
          >
            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <div className="sui-rounded-md sui-bg-[linear-gradient(90deg,#E0F8ED_0%,#9CE8C4_100%)] sui-p-2">
              <Typography variant="body-sm">
                <>
                  <span className="sui-font-bold sui-text-success">FREE</span>{' '}
                  <span className="sui-font-bold">Delivery Today</span>{' '}
                  with {formatDollarAmount(fsdbmProgramInclusion?.threshold, { hideCents: true })}+ of eligible items
                </>
              </Typography>
            </div>
          </div>
        )}

        {sameDayPriorityOption && (
          <div className="sui-flex sui-items-center sui-justify-between sui-px-3 sui-py-2">
            <Typography variant="body-base">
              <span className="sui-font-bold">Today</span> by {bodfsLatestDelivery}
            </Typography>
            <Typography color="primary" variant="body-base" weight="regular">
              {formatDollarAmount(sameDayPriorityOption?.totalCharge)}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

PriorityDeliveryMessageCard.propTypes = {
  additionalPriorityDeliveryOptions: PropTypes.arrayOf(PropTypes.object),
  bodfsLatestDelivery: PropTypes.string,
  fsdbmProgramInclusion: PropTypes.shape({
    description: PropTypes.string,
    threshold: PropTypes.string
  }),
  sameDayPriorityOption: PropTypes.object
};
